import React, { useState, useEffect } from 'react';
import { Edit2, Trash2 } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { getAdminCategories, addCategory, deleteCategory, updateCategory } from '../config/api';

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [name,       setName]       = useState('');
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [deleteId,   setDeleteId]   = useState(null);
  const [deleteName, setDeleteName] = useState('');
  const [loading,    setLoading]    = useState(false);

  const load = async () => {
    try {
      const data = await getAdminCategories();
      setCategories(Array.isArray(data.data?.data) ? data.data.data : []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    setError(''); setSuccess('');
    if (!name.trim() || name.trim().length < 2) { setError('Name must be at least 2 characters'); return; }
    setLoading(true);
    try {
      await addCategory(name.trim());
      setName('');
      setSuccess('Category added successfully');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add category');
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteCategory(deleteId);
      setDeleteId(null);
      setDeleteName('');
      await load();
    } catch (err) {
      setDeleteId(null);
      setError(err.response?.data?.error || 'Failed to delete category');
    }
  };

  const handleShowOnHomepageToggle = async (categoryId) => {
    try {
      // Find current state of this category
      const category = categories.find(c => c.id === categoryId);
      if (!category) return; // Category not found, bail out
      const newValue = !category.show_on_homepage;

      // Optimistic UI update
      setCategories(cats => cats.map(c => ({
        ...c,
        show_on_homepage: c.id === categoryId ? newValue : false
      })));

      // Send PATCH request
      await updateCategory(categoryId, { show_on_homepage: newValue });

      // Don't reload - optimistic update is sufficient, backend enforces single-selection
    } catch (err) {
      console.error('Failed to update category:', err);
      setError(err.response?.data?.error || 'Failed to update category');
      // Reload to revert optimistic update
      await load();
    }
  };

  const inputStyle = {
    flex: 1,
    border: 'none',
    borderBottom: '1px solid var(--color-border)',
    background: 'transparent',
    padding: '9px 0',
    fontSize: 14,
    color: 'var(--color-text)',
    outline: 'none',
    fontFamily: 'var(--font-body)',
  };

  return (
    <AdminLayout title="Category Management">

      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 24, marginBottom: 28 }}>
        <div style={{ fontSize: 12, fontFamily: 'var(--font-body)', color: 'var(--color-muted)', marginBottom: 14, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Add New Category
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <input
            style={inputStyle}
            value={name}
            onChange={e => { setName(e.target.value); setError(''); setSuccess(''); }}
            placeholder="Category name..."
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
          />
          <button
            onClick={handleAdd}
            disabled={loading}
            style={{ padding: '10px 20px', background: 'var(--color-accent)', color: '#fff', border: 'none', fontSize: 12, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)', borderRadius: 'var(--border-radius)', whiteSpace: 'nowrap' }}
          >
            {loading ? 'Adding...' : 'Add +'}
          </button>
        </div>

        {error && (
          <p style={{ marginTop: 10, fontSize: 12, color: 'var(--color-error)', fontFamily: 'var(--font-body)' }}>{error}</p>
        )}
        {success && (
          <p style={{ marginTop: 10, fontSize: 12, color: 'var(--color-success)', fontFamily: 'var(--font-body)' }}>{success}</p>
        )}
      </div>

      <table className="admin-table-desktop" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'var(--font-body)' }}>
        <thead>
          <tr>
            {['Name', 'Show on Homepage', 'Photos', 'Created', 'Action'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-muted)', borderBottom: '1px solid var(--color-border)' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {categories.map(cat => (
            <tr key={cat.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '12px', fontWeight: 500, color: 'var(--color-text)' }}>
                {cat.name}
                {cat.is_default === 1 && (
                  <span style={{
                    marginLeft: 8,
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 9,
                    background: '#fb923c',
                    color: '#fff',
                    letterSpacing: '0.05em',
                    fontWeight: 600
                  }}>
                    DEFAULT
                  </span>
                )}
              </td>
              <td style={{ padding: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={cat.show_on_homepage || false}
                    onChange={() => handleShowOnHomepageToggle(cat.id)}
                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                  />
                  <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>
                    {cat.show_on_homepage ? 'Yes' : 'No'}
                  </span>
                </label>
              </td>
              <td style={{ padding: '12px', color: 'var(--color-muted)' }}>{cat.photo_count}</td>
              <td style={{ padding: '12px', color: 'var(--color-muted)', fontSize: 11 }}>
                {new Date(cat.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
              </td>
              <td style={{ padding: '12px' }}>
                {cat.is_default === 1 ? (
                  <button
                    disabled
                    title="Default category cannot be deleted"
                    style={{ padding: '4px 10px', border: '1px solid var(--color-border)', color: 'var(--color-muted)', background: 'transparent', fontSize: 11, cursor: 'not-allowed', borderRadius: 4, opacity: 0.5, fontFamily: 'var(--font-body)' }}
                  >
                    Delete
                  </button>
                ) : cat.photo_count > 0 ? (
                  <button
                    disabled
                    title="Remove all photos from this category first"
                    style={{ padding: '4px 10px', border: '1px solid var(--color-border)', color: 'var(--color-muted)', background: 'transparent', fontSize: 11, cursor: 'not-allowed', borderRadius: 4, opacity: 0.5, fontFamily: 'var(--font-body)' }}
                  >
                    Delete
                  </button>
                ) : (
                  <button
                    onClick={() => { setDeleteId(cat.id); setDeleteName(cat.name); }}
                    style={{ padding: '4px 10px', border: '1px solid #fca5a5', color: 'var(--color-error)', background: 'transparent', fontSize: 11, cursor: 'pointer', borderRadius: 4, fontFamily: 'var(--font-body)' }}
                  >
                    Delete
                  </button>
                )}
              </td>
            </tr>
          ))}
          {categories.length === 0 && (
            <tr>
              <td colSpan={5} style={{ padding: 32, textAlign: 'center', color: 'var(--color-muted)', fontFamily: 'var(--font-body)' }}>
                No categories yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Mobile Card Layout */}
      <div className="admin-cards-mobile">
        {categories.map(cat => (
          <div key={cat.id} className="category-card">
            <div className="category-card-info">
              <div className="category-card-name">
                {cat.name}
                {cat.is_default === 1 && (
                  <span style={{
                    marginLeft: 8,
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 9,
                    background: '#fb923c',
                    color: '#fff',
                    letterSpacing: '0.05em',
                    fontWeight: 600
                  }}>
                    DEFAULT
                  </span>
                )}
              </div>
              <div style={{ marginTop: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={cat.show_on_homepage || false}
                    onChange={() => handleShowOnHomepageToggle(cat.id)}
                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                  />
                  <span style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>
                    Show on Homepage
                  </span>
                </label>
              </div>
              <div className="category-card-meta">
                {cat.photo_count} photo{cat.photo_count !== 1 ? 's' : ''}
              </div>
            </div>

            <div className="category-card-actions">
              <button
                className="category-card-icon-btn"
                disabled
                title="Edit not yet implemented"
              >
                <Edit2 size={20} />
              </button>
              {cat.is_default === 1 ? (
                <button
                  className="category-card-icon-btn"
                  disabled
                  title="Default category cannot be deleted"
                >
                  <Trash2 size={20} />
                </button>
              ) : cat.photo_count > 0 ? (
                <button
                  className="category-card-icon-btn"
                  disabled
                  title="Remove all photos from this category first"
                >
                  <Trash2 size={20} />
                </button>
              ) : (
                <button
                  className="category-card-icon-btn delete"
                  onClick={() => { setDeleteId(cat.id); setDeleteName(cat.name); }}
                >
                  <Trash2 size={20} />
                </button>
              )}
            </div>
          </div>
        ))}

        {categories.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-muted)', fontFamily: 'var(--font-body)' }}>
            No categories yet.
          </div>
        )}
      </div>

      {deleteId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="delete-modal-content" style={{ background: 'var(--color-surface)', padding: 32, maxWidth: 400, width: '90%', borderRadius: 4 }}>
            <h3 style={{ fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 500, color: 'var(--color-text)', marginBottom: 10 }}>
              Delete "{deleteName}"?
            </h3>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-muted)', marginBottom: 24 }}>
              This cannot be undone.
            </p>
            <div className="delete-modal-buttons" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setDeleteId(null); setDeleteName(''); }}
                style={{ padding: '9px 20px', border: '1px solid var(--color-border)', background: 'transparent', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-body)', borderRadius: 'var(--border-radius)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                style={{ padding: '9px 20px', background: 'var(--color-error)', color: '#fff', border: 'none', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-body)', borderRadius: 'var(--border-radius)' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </AdminLayout>
  );
}